import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight, ShieldCheck, Ruler, PencilRuler, HardHat, Scale } from "lucide-react";
import { MaskedLines } from "./Reveal";
import { IMAGES, BRAND } from "../../data/content";
import { openBooking } from "../../lib/booking";

const EASE = [0.22, 1, 0.36, 1];

const PANEL = [
  { label: "Surveyors", Icon: Ruler, img: IMAGES.surveyor },
  { label: "Architects", Icon: PencilRuler, img: IMAGES.architect },
  { label: "Engineers", Icon: HardHat, img: IMAGES.engineer },
  { label: "Lawyers", Icon: Scale, img: IMAGES.lawyer },
];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.18]);
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "12%"]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen bg-ov-bone pt-28 pb-16 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10 items-center min-h-[calc(100vh-8rem)]">
        {/* Left: kinetic type */}
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex items-center gap-3 mb-8"
          >
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-ov-green">
              <ShieldCheck size={16} /> Property Compliance Panel
            </span>
          </motion.div>

          <h1 className="font-display text-ov-navy text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
            <MaskedLines lines={["Don't just", "buy a property."]} delay={0.25} />
            <span className="block mt-2 italic text-ov-green">
              <MaskedLines lines={["Verify & Buy."]} delay={0.55} />
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9, ease: EASE }}
            className="font-body text-base md:text-lg text-ov-charcoal/70 max-w-xl mt-8 leading-relaxed"
          >
            India&apos;s expert panel of surveyors, architects, engineers and lawyers —
            verifying every title, boundary and permission before you invest. Safe
            assets, safe future.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.05, ease: EASE }}
            className="flex flex-wrap items-center gap-4 mt-10"
          >
            <button
              onClick={() => openBooking()}
              data-testid="hero-cta-primary"
              className="group inline-flex items-center gap-2 bg-ov-navy text-white text-sm font-medium px-8 py-4 rounded-full hover:bg-ov-green transition-colors duration-300"
            >
              Start Your Verification
              <ArrowDownRight size={18} className="group-hover:translate-x-0.5 group-hover:translate-y-0.5 transition-transform" />
            </button>
            <a
              href="#services"
              data-testid="hero-cta-secondary"
              className="inline-flex items-center gap-2 text-sm font-medium text-ov-navy px-6 py-4 rounded-full border border-ov-navy/20 hover:border-ov-green hover:text-ov-green transition-colors duration-300"
            >
              Explore Services
            </a>
          </motion.div>

          {/* Expert Panel highlight — with photos */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.7, ease: EASE }}
            data-testid="hero-expert-panel"
            className="mt-12"
          >
            <div className="inline-flex items-center gap-2 mb-5">
              <span className="h-px w-8 bg-ov-green" />
              <span className="font-body text-xs uppercase tracking-[0.2em] font-semibold text-ov-navy">
                Meet our Expert Panel
              </span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-5 gap-y-5">
              {PANEL.map(({ label, Icon, img }, i) => (
                <motion.div
                  key={label}
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.85 + i * 0.1, ease: EASE }}
                  data-testid={`hero-panel-${label.toLowerCase()}`}
                  className="group flex items-center gap-3"
                >
                  <div className="relative h-14 w-14 flex-shrink-0 rounded-full overflow-hidden ring-2 ring-white shadow-md shadow-ov-navy/10">
                    <img
                      src={img}
                      alt={label}
                      className="h-full w-full object-cover grayscale-[25%] group-hover:grayscale-0 group-hover:scale-110 transition-all duration-500"
                    />
                    <span className="absolute -bottom-0.5 -right-0.5 h-6 w-6 rounded-full bg-ov-green flex items-center justify-center ring-2 ring-white">
                      <Icon size={11} className="text-white" strokeWidth={2.5} />
                    </span>
                  </div>
                  <span className="font-body text-sm font-semibold text-ov-navy leading-tight">{label}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.3 }}
            className="flex items-center gap-8 mt-12"
          >
            {[
              ["100%", "Verified Reports"],
              ["4", "Expert Disciplines"],
              ["9", "Compliance Checks"],
            ].map(([n, l]) => (
              <div key={l}>
                <div className="font-display text-3xl text-ov-navy">{n}</div>
                <div className="font-body text-xs uppercase tracking-wider text-ov-charcoal/50 mt-1">{l}</div>
              </div>
            ))}
          </motion.div>
        </div>

        {/* Right: clipped parallax image */}
        <div className="lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, delay: 0.5, ease: EASE }}
            className="relative h-[52vh] lg:h-[70vh] overflow-hidden rounded-t-[140px] rounded-b-3xl border border-ov-navy/10"
          >
            <motion.img
              src={IMAGES.hero}
              alt="Luxury verified property"
              style={{ scale: imgScale, y: imgY }}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ov-navy/40 via-transparent to-transparent" />

            {/* Verified badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8, y: -12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 1.05, ease: EASE }}
              data-testid="hero-verified-badge"
              className="absolute top-6 left-6 flex items-center gap-2.5 bg-white rounded-full pl-2 pr-4 py-2 shadow-xl shadow-ov-navy/10"
            >
              <span className="relative flex h-9 w-9 items-center justify-center rounded-full bg-ov-green">
                <ShieldCheck size={17} className="text-white" />
                <span className="absolute inset-0 rounded-full bg-ov-green animate-ping opacity-40" />
              </span>
              <div className="leading-tight">
                <div className="font-body text-[10px] uppercase tracking-[0.15em] text-ov-charcoal/50">Verified</div>
                <div className="font-body text-sm font-semibold text-ov-navy">Safe Home</div>
              </div>
            </motion.div>

            {/* ONEVISION logo brand chip */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8, y: -12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 1.2, ease: EASE }}
              data-testid="hero-logo-chip"
              className="absolute top-6 right-6 bg-white rounded-2xl px-3.5 py-2.5 shadow-xl shadow-ov-navy/10"
            >
              <img src={BRAND.logo} alt="ONE VISION" className="h-8 w-auto" />
            </motion.div>

            <div className="absolute bottom-6 left-6 right-6 bg-white/85 backdrop-blur-md rounded-2xl px-5 py-4">
              <div className="font-body text-xs uppercase tracking-[0.2em] text-ov-green font-semibold">
                {BRAND.tagline}
              </div>
              <div className="font-display text-lg text-ov-navy mt-1">
                Compliance today, peace of mind forever.
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 1 }}
        className="hidden lg:flex absolute bottom-8 left-1/2 -translate-x-1/2 items-center gap-2 text-ov-charcoal/40 text-xs uppercase tracking-[0.3em]"
      >
        <motion.span
          animate={{ y: [0, 6, 0] }}
          transition={{ repeat: Infinity, duration: 1.8 }}
        >
          Scroll
        </motion.span>
      </motion.div>
    </section>
  );
}
